import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ClockIcon, MapPinIcon } from "@phosphor-icons/react";
import { formatDateWithOptionsBs } from "@/lib/format";
import type { ServiceInfo } from "./types";
import { toLocalDateString } from "@/lib/schedule-overview";
import { SessionTime } from "./SessionTime";

function fmtKm(n: number): string {
	return `${new Intl.NumberFormat("bs-BA", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)} KM`;
}

/**
 * Cijena na kartici termina. Kod sufinansiranja kartica nosi "Plaća: Firma (X)"
 * i "Iznos za plaćanje: Y", pa gore ide zbir X + Y (ukupno sa rabatom firme),
 * a ne cijena iz liste: inače se tri broja na kartici ne slažu, a rabat se
 * nigdje ne objašnjava.
 */
export function ticketPriceLabel(args: {
	service: ServiceInfo;
	paymentMethod: "monri" | "bank_transfer" | "company_credit";
	companyPayment?: { chargeAmount: number } | null;
	chargeAmount?: number | null;
}): string {
	const { service, paymentMethod, companyPayment, chargeAmount } = args;
	if (!companyPayment) return `${service.price} KM`;
	if (paymentMethod === "company_credit") return fmtKm(companyPayment.chargeAmount);
	if (chargeAmount != null) return fmtKm(companyPayment.chargeAmount + chargeAmount);
	return `${service.price} KM`;
}

/** True when the charged amount and the listed price differ by at least a fening. */
export function chargeDiffersFromList(chargeAmount: number, listPrice: number): boolean {
	return Math.round(chargeAmount * 100) !== Math.round(listPrice * 100);
}

function Row({ label, value }: { label: string; value: ReactNode }) {
	return (
		<div className="flex items-center justify-between gap-4 py-2.5">
			<span className="text-sm text-muted-foreground">{label}</span>
			<span className="text-right text-sm font-medium text-foreground">{value}</span>
		</div>
	);
}

export interface AppointmentTicketProps {
	service: ServiceInfo;
	date: Date;
	time: string;
	methodLabel: string;
	priceLabel: string;
	companyLine?: string | null;
	/**
	 * Server-computed amount the booking will actually charge (card / bank
	 * transfer), from /api/sessions/price-quote. Shown as "Iznos za plaćanje";
	 * when it differs from the listed price a short explanation follows.
	 */
	chargeAmount?: number | null;
	modality: "online" | "in_person";
	officeAddress?: string | null;
	/** "full" = bordered ticket card; "slim" = one-line summary chip. */
	variant?: "full" | "slim";
}

export function AppointmentTicket({
	service, date, time, methodLabel, priceLabel, companyLine, chargeAmount,
	modality, officeAddress, variant = "full",
}: AppointmentTicketProps) {
	const { t } = useTranslation();
	// `date` je lokalna ponoć sarajevskog kalendarskog dana (CalendarGrid).
	const dateStr = toLocalDateString(date);
	const dateLabel = formatDateWithOptionsBs(date, {
		weekday: "short", day: "numeric", month: "long",
	});

	if (variant === "slim") {
		return (
			<div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-border bg-muted/50 px-3.5 py-2.5 text-xs text-muted-foreground">
				<span className="font-semibold text-foreground">{service.name}</span>
				<span className="text-border">·</span><span>{dateLabel}</span>
				<span className="text-border">·</span><span><SessionTime dateStr={dateStr} time={time} /></span>
				<span className="text-border">·</span>
				<span className="font-semibold text-foreground">
					{chargeAmount != null ? fmtKm(chargeAmount) : priceLabel}
				</span>
			</div>
		);
	}

	return (
		<div className="overflow-hidden rounded-2xl border border-border bg-card">
			<div className="flex items-center gap-3 border-b border-border bg-muted/50 px-4 py-3.5">
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-semibold text-foreground"
					   style={{ fontFamily: "var(--font-heading)" }}>
						{service.name}
					</p>
					<p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
						<ClockIcon className="size-3.5" />
						{service.durationMinutes} min
					</p>
				</div>
			</div>

			<div className="divide-y divide-border/60 px-4">
				<Row label={t("booking.dateLabel")} value={dateLabel} />
				<Row label={t("booking.timeLabel")} value={<SessionTime dateStr={dateStr} time={time} />} />
				<Row label={t("booking.paymentMethodLabel")} value={methodLabel} />

				<Row
					label={t("booking.sessionTypeLabel")}
					value={
						modality === "in_person" ? (
							<span className="flex flex-col items-end gap-0.5">
								<span>{t("booking.modalityInPerson")}</span>
								{officeAddress && (
									<span className="flex items-center gap-1 text-xs font-normal text-muted-foreground">
										<MapPinIcon className="size-3" />
										{officeAddress}
									</span>
								)}
							</span>
						) : (
							t("booking.modalityOnline")
						)
					}
				/>
			</div>

			<div className="border-t border-dashed border-border bg-muted/50 px-4 py-3.5">
				<div className="flex items-baseline justify-between">
					<span className="text-sm font-semibold text-foreground">{t("booking.priceLabel")}</span>
					<span className="text-xl font-bold text-foreground"
					      style={{ fontFamily: "var(--font-heading)" }}>
						{priceLabel}
					</span>
				</div>
				{companyLine && (
					<p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">{companyLine}</p>
				)}
				{chargeAmount != null && (
					<div data-testid="booking-amount-to-pay" className="mt-2">
						<p className="text-sm font-semibold text-foreground">
							{t("booking.amountToPay", { amount: fmtKm(chargeAmount) })}
						</p>
						{/* Kod sufinansiranja razliku objašnjava linija firme, ne zemlja konekcije. */}
						{!companyLine && chargeDiffersFromList(chargeAmount, service.price) && (
							<p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
								{t("booking.amountByConnection")}
							</p>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

export { fmtKm };
